import React, { useCallback, useEffect, useRef, useState } from 'react';
import WebFont from 'webfontloader';
import { TEMPLATE_DIR } from '../../config';
import dayjs from 'dayjs';

// canvas setting
let c: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;

// values
let from: string = '';
let to: string = '';
let message: string = '';
let oldMessage: string = '';
let template: string = '';
let templateElement: HTMLImageElement | null = null;
// let lineColor: string = '';

// font settings
const FONT = {
  NAME_SIZE: 'normal 30px',
  MESSAGE_SIZE: 'normal 25px',
  SMALL_SIZE: 'normal 24px',
  FAMILIY: "'Noto Sans JP', sans-serif",
  LINE_HEIGHT: 42,
  getNameSet() {
    return this.NAME_SIZE + ' ' + this.FAMILIY;
  },
  getMessageSet() {
    return this.MESSAGE_SIZE + ' ' + this.FAMILIY;
  },
  getSmallSet() {
    return this.SMALL_SIZE + ' ' + this.FAMILIY;
  },
};

// textarea on Canvas
const CONTENT_AREA = {
  TOP: 160,
  LEFT: 110,
  RIGHT: 0,
  BOTTOM: 0,
  WIDTH: 0,
};

// settings
const LINE_WIDTH = 2;
const MAX_LINES = 7;

let messageLine: string[] = [];

interface LetterCanvasProps {
  template_file_name: string;
  from_name: string;
  to_name: string;
  message: string;
  date: string;
  onOutput: (url: string) => void;
}

const LetterCanvas: React.FC<LetterCanvasProps> = ({
  template_file_name,
  from_name,
  to_name,
  message: propMessage,
  date,
  onOutput,
}) => {
  const [downloadURL, setDownloadURL] = useState('#');
  const [outputOpacity, setOutputOpacity] = useState(0);

  const drawTo = useCallback(() => {
    if (!to || !ctx) return;

    const name = to + 'へ';
    let w: number, h: number;

    ctx.save();

    ctx.font = FONT.getNameSet();
    ctx.textBaseline = 'middle';
    ctx.fillText(name, CONTENT_AREA.LEFT, CONTENT_AREA.TOP + 14);

    // h = 38;
    // w = ctx.measureText(name).width;

    // ctx.strokeStyle = lineColor;
    // ctx.beginPath();
    // ctx.moveTo(CONTENT_AREA.LEFT, CONTENT_AREA.TOP + h);
    // ctx.lineTo(CONTENT_AREA.LEFT + w, CONTENT_AREA.TOP + h);
    // ctx.closePath();
    // ctx.stroke();
    ctx.restore();
  }, []);

  const drawFrom = useCallback(() => {
    if (!from || !ctx) return;

    let w: number, h: number, text: string;

    ctx.save();
    ctx.font = FONT.getSmallSet();
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';

    text = 'より';
    ctx.fillText(text, CONTENT_AREA.RIGHT + 10, CONTENT_AREA.BOTTOM - 11);
    w = ctx.measureText(text).width;

    ctx.font = FONT.getNameSet();
    ctx.fillText(from, CONTENT_AREA.RIGHT + 10 - w, CONTENT_AREA.BOTTOM - 14);
    w += ctx.measureText(from).width;

    // ctx.strokeStyle = lineColor;
    // ctx.beginPath();
    // ctx.moveTo(CONTENT_AREA.RIGHT + 10, CONTENT_AREA.BOTTOM + 10);
    // ctx.lineTo(CONTENT_AREA.RIGHT + 10 - w, CONTENT_AREA.BOTTOM + 10);
    // ctx.closePath();
    // ctx.stroke();
    ctx.restore();
  }, []);

  const makeMessageLine = useCallback((newMessage: string) => {
    if (!ctx) return;

    let tempMessageLine: string[] = [];
    let i = 0;
    const newMessageLine = newMessage.split('');
    newMessageLine.forEach((val) => {
      if (val !== '\n') {
        if (tempMessageLine[i]) {
          tempMessageLine[i] += val;
        } else {
          tempMessageLine[i] = val;
        }

        const w = (ctx as CanvasRenderingContext2D).measureText(
          tempMessageLine[i]
        ).width;

        if (w > CONTENT_AREA.WIDTH) {
          i++;
        }
      } else {
        i++;
      }
    });

    messageLine = tempMessageLine;
    messageLine.splice(MAX_LINES);
  }, []);

  const drawMessage = useCallback(() => {
    if (!ctx) return;

    let h: number;
    ctx.save();
    ctx.font = FONT.getMessageSet();
    ctx.textBaseline = 'middle';
    h = FONT.LINE_HEIGHT;

    messageLine.forEach((message, i) => {
      ctx!.fillText(message, CONTENT_AREA.LEFT, CONTENT_AREA.TOP + 85 + h * i);
    });
    ctx.restore();
  }, []);

  const loadTemplate = useCallback(() => {
    return new Promise<void>((resolve) => {
      template = TEMPLATE_DIR + '/' + template_file_name;

      console.log('template', template);
      // lineColor = line_color;
      const img = new Image();
      img.onload = () => {
        templateElement = img;
        resolve();
      };
      img.src = template;
    });
  }, [template_file_name]);

  const drawTemplate = useCallback(() => {
    if (!ctx || !templateElement || !c) return;
    ctx.drawImage(templateElement, 0, 0, 700, 700);
  }, []);

  const getDate = useCallback((date: string) => {
    let format = 'YYYY.MM.DD';
    const dateObj = new Date(date);

    format = format.replace(/YYYY/g, dateObj.getFullYear().toString());
    format = format.replace(/MM/g, (dateObj.getMonth() + 1).toString());
    format = format.replace(/DD/g, dateObj.getDate().toString());

    return format;
  }, []);

  const drawDate = useCallback(() => {
    if (!ctx) return;

    ctx.save();
    ctx.font = FONT.getSmallSet();
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';
    ctx.fillStyle = '#555555';
    ctx.fillText(
      dayjs(date).format('YYYY.MM.DD'),
      CONTENT_AREA.RIGHT + 10,
      140
    );
    ctx.restore();
  }, [date]);

  const output = useCallback(() => {
    if (!c) return;
    const url = c.toDataURL();
    setDownloadURL(url);
    setOutputOpacity(1);
    onOutput(url);
  }, [onOutput]);

  const draw = useCallback(() => {
    console.log('draw');
    drawTemplate();
    drawFrom();
    drawTo();
    drawMessage();
    drawDate();
    output();
  }, [drawTemplate, drawFrom, drawTo, drawMessage, drawDate, output]);

  const getBrowser = useCallback(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();

    if (
      userAgent.indexOf('msie') !== -1 ||
      userAgent.indexOf('trident') !== -1
    ) {
      return 'ie';
    } else if (userAgent.indexOf('edge') !== -1) {
      return 'edge';
    } else if (userAgent.indexOf('chrome') !== -1) {
      return 'chrome';
    } else if (userAgent.indexOf('safari') !== -1) {
      return 'safari';
    } else if (userAgent.indexOf('firefox') !== -1) {
      return 'firefox';
    } else if (userAgent.indexOf('opera') !== -1) {
      return 'opera';
    } else {
      return null;
    }
  }, []);

  const init = useCallback(() => {
    c = document.getElementById('js-canvas') as HTMLCanvasElement;
    if (!c) return;
    ctx = c.getContext('2d');
    if (!ctx) return;

    // 固定サイズでCanvasを設定（700x700）
    const canvasSize = 700;
    const devicePixelRatio = window.devicePixelRatio || 1;

    // Canvasの実際のサイズを固定サイズの2倍に設定
    c.width = canvasSize * devicePixelRatio;
    c.height = canvasSize * devicePixelRatio;

    // コンテキストをスケール
    ctx.scale(devicePixelRatio, devicePixelRatio);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    ctx.fillStyle = '#101010';
    ctx.lineWidth = LINE_WIDTH;
    ctx.font = FONT.getMessageSet();

    if (getBrowser() === 'firefox') {
      FONT.FAMILIY = 'sans-serif';
    }

    // parameter setting
    from = from_name;
    to = to_name;
    message = propMessage;
    template = TEMPLATE_DIR + '/' + template_file_name;
    templateElement = null;
    // lineColor = line_color;

    CONTENT_AREA.RIGHT = canvasSize - 112;
    CONTENT_AREA.BOTTOM = canvasSize - 140;
    CONTENT_AREA.WIDTH = CONTENT_AREA.RIGHT - CONTENT_AREA.LEFT;

    makeMessageLine(propMessage);
    loadTemplate().then(draw);
  }, [
    from_name,
    to_name,
    propMessage,
    template_file_name,
    getBrowser,
    makeMessageLine,
    loadTemplate,
    draw,
  ]);

  useEffect(() => {
    WebFont.load({
      custom: {
        families: ['Noto Sans JP'],
        urls: ['https://fonts.googleapis.com/css?family=Noto+Sans+JP'],
      },
      active: () => {
        init();
      },
      inactive: () => {
        init();
      },
    });
  }, [init]);

  useEffect(() => {
    from = from_name;
    draw();
  }, [draw, from_name]);

  useEffect(() => {
    to = to_name;
    draw();
  }, [draw, to_name]);

  useEffect(() => {
    message = propMessage;
    makeMessageLine(propMessage);
    draw();
  }, [draw, propMessage, makeMessageLine]);

  return (
    <div className='p-canvas-area'>
      <canvas
        id='js-canvas'
        width='700'
        height='700'
        style={{
          width: '100%',
          maxWidth: '700px',
          height: 'auto',
          aspectRatio: '1 / 1',
        }}
      ></canvas>
      <img
        src={downloadURL}
        style={{
          opacity: outputOpacity,
          width: '100%',
          maxWidth: '700px',
          height: 'auto',
          aspectRatio: '1 / 1',
        }}
        alt='letter'
        id='output'
      />
    </div>
  );
};

export default LetterCanvas;
